import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiCheckboxCircleFill, RiCheckLine, RiPlaneLine, RiBuildingLine, RiTrophyLine, RiMedalLine, RiPriceTag3Line } from '@remixicon/react';
import Button from '@components/ui/Button';
import { TrustJetPlane, TrustJetLogoV2 } from '@assets/images';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/constants';
import { formatPrice, trunc2 } from '@/utils';
import { selectPrimaryBidOption } from '@/features/quotes/utils';
import { Skeleton } from '@components/ui/Skeleton';
import { getAircraftModelName, getOperatorWithLocation, getEffectiveAircraft } from '@/features/tails/utils';
/**
 * BookingConfirmed Page Component
 * Displays booking confirmation with aircraft details after successful payment
 */
const BookingConfirmed = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const { userProfile } = useAuthStore();
  const quoteId = searchParams.get('quoteId');
  const isTokenBasedCheckout = searchParams.get('token');
  const optionId = searchParams.get('option');
  const referenceId = searchParams.get('referenceId');
  const customMessage = searchParams.get('message');

  // Invoice flow re-uses the `quoteId` URL param as the invoice id, and
  // encodes the flow via `option=invoiceaddservice` / `option=invoiceoneoff`.
  const isInvoiceFlow = optionId === 'invoiceaddservice' || optionId === 'invoiceoneoff';
  const invoiceId = isInvoiceFlow ? quoteId : null;

  // Fetch quote details using React Query (must be before early return)
  const { data: quote, isLoading, error: quoteError } = useQuery({
    queryKey: ['quote', quoteId, isTokenBasedCheckout, optionId],
    queryFn: async () => {
      let data;
      if (isTokenBasedCheckout) {
        // Use token-based API for public access
        data = await apiClient.getQuoteByIdWithToken(quoteId, isTokenBasedCheckout);
      } else {
        // Use authenticated API
        data = await apiClient.getQuoteById(quoteId);
      }
      return data;
    },
    enabled: !!quoteId && !isInvoiceFlow,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time page loads
    retry: 2,
    retryDelay: 1000,
  });

  const { data: invoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice', invoiceId, isTokenBasedCheckout],
    queryFn: () =>
      apiClient.getInvoiceById(invoiceId, isTokenBasedCheckout || null, { include: 'items,client' }),
    enabled: !!invoiceId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const taxesFeesNoCard = isInvoiceFlow ? trunc2(Number(invoice?.tax_amount) + Number(invoice?.platform_fee)) : 0
  const subtotal = isInvoiceFlow ? trunc2(taxesFeesNoCard + (Number(invoice?.subtotal))) : 0;
  const taxesFeesWithCard = isInvoiceFlow ? trunc2(taxesFeesNoCard + (subtotal * 0.03)) : 0;

  console.log("confirmedquote", quote);

  // Extract the selected bid option from quote data (works for both token and authenticated)
  const quoteBidOption = useMemo(() => {
    if (!quote) return null;
    // If optionId is provided, find the matching bid option — this is the
    // authoritative path (checkout URL carries the exact option the client
    // paid for) and covers ~every real render of this page.
    if (optionId) {
      for (const bidItem of (quote.bid_items || [])) {
        const options = bidItem.bid?.bid_options || bidItem.bid_options || [];
        const matchingOption = options.find(
          option => option.bid_option_id === optionId
        );
        if (matchingOption) return matchingOption;
      }
    }
    // Fallback for the rare "arrived here without optionId" case (deep-
    // link with stale URL, refresh after clearing the query string).
    // Flatten ALL bid_items' options into one list first, THEN pick —
    // otherwise a per-bidItem loop could return a shortlisted DRAFT
    // from bid_items[0] and miss an ACCEPTED option living in [1].
    // Use `.length ? nested : alt` (not `||`) so an empty nested array
    // doesn't shadow a populated legacy-shape `bid_options` at the same
    // level.
    const allOptions = (quote.bid_items || []).flatMap((bi) => {
      const nested = bi?.bid?.bid_options;
      if (Array.isArray(nested) && nested.length) return nested;
      const legacy = bi?.bid_options;
      return Array.isArray(legacy) ? legacy : [];
    });
    return selectPrimaryBidOption(allOptions);
  }, [quote, optionId]);


  // Fetch organization name
  const orgId = userProfile?.org_id;
  const { data: organization } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => apiClient.getOrganizationById(orgId),
    enabled: !!orgId && !isTokenBasedCheckout,
  });

  // Extract aircraft data: prefer quote API data, fallback to store.
  // Per-quote overrides from quote.files.aircraft_overrides win over the
  // base aircraft record — that's why aircraftDetails goes through
  // getEffectiveAircraft here, not straight to aircraft.aircraft.
  const aircraft = quoteBidOption || {};
  const aircraftDetails = getEffectiveAircraft(aircraft.aircraft, quote) || {};
  const operator = aircraft.operator || aircraftDetails.operator || {};
  // Read features from the MERGED aircraft, not the bidOption.
  // The bidOption's aircraft_features is a small bag of booleans
  // (`{beds, wifi, entertainment}`), while the override fans out into
  // aircraftDetails.aircraft_features.custom_features via
  // getEffectiveAircraft. Reading from the bidOption meant an override
  // full of amenities still rendered "No amenities included." Fall back
  // to bidOption.aircraft_features if the merged record has none, so
  // aircraft without an override still surface the base features.
  const features = aircraftDetails.aircraft_features || aircraft.aircraft_features || {};

  const handleFinish = async() => {
    // Invalidate quotes cache to refresh data
    await queryClient.invalidateQueries({ queryKey: ['quotes'] });
    navigate(ROUTES.QUOTES.path);
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${TrustJetPlane})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <img src={TrustJetLogoV2} alt="TrustJet" className="h-14" />
        </div>

        {/* Transparent Container Box */}
        <div className="bg-white/20 backdrop-blur-md rounded-3xl p-6 w-full">
          {/* Confirmation Title */}
          {(quote || invoice) && (
            <>
              <h1 className="text-3xl md:text-4xl font-semibold text-white mb-2">
                {customMessage || (isInvoiceFlow ? 'Invoice payment confirmed!' : 'Your booking is confirmed!')}
              </h1>
              {organization?.name && (
                <p className="text-white/80 mb-4 text-sm">
                  Thank you for booking with <span className="font-medium text-white">{organization.name}</span>.
                </p>
              )}
              {referenceId && (
                <div className="flex gap-2 bg-green-50 text-green-800 rounded-full px-4 py-2 mb-4 w-full">
                  <RiCheckboxCircleFill className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {isInvoiceFlow ? `Your invoice reference is ${referenceId}` : `Your booking number is ${referenceId}`}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Booking Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full overflow-hidden">
            {(isInvoiceFlow ? (invoiceLoading || !invoice) : (isLoading || !quote)) ? (
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-28" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                  <Skeleton className="h-12 w-12 rounded-full" />
                </div>
                <Skeleton className="h-7 w-24 rounded-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
            ) : isInvoiceFlow ? (
              /* Invoice Info Section */
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Invoice #{invoice.invoice_number || invoice.invoice_id}
                    </h2>
                    {invoice.client && (
                      <p className="text-sm text-gray-500 mt-1">
                        {invoice.client.first_name} {invoice.client.last_name}
                      </p>
                    )}
                  </div>
                  <div className="bg-accent text-primary-500 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <RiPriceTag3Line className="w-4 h-4" />
                    <span>{formatPrice(trunc2(invoice.total_amount))}</span>
                  </div>
                </div>

                {(invoice.items || []).length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Services</p>
                    <div className="space-y-2">
                      {invoice.items.map((svc, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-700">{svc.item}</span>
                          <span className="text-gray-900 font-medium">{formatPrice(trunc2(svc.amount))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t border-gray-100 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span>
                    <span>{formatPrice(trunc2(invoice.subtotal))}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Taxes and Fees</span>
                    <span>{formatPrice(taxesFeesWithCard)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                    <span>Total</span>
                    <span>{formatPrice(trunc2(invoice.total_amount))}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Aircraft Info Section */
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {aircraftDetails.tail_number || 'N123GT'}
                    </h2>
                    <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                      <RiPlaneLine className="w-4 h-4" />
                      <span>{getAircraftModelName(aircraftDetails)}</span>
                    </div>
                    {/* <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                      <RiBuildingLine className="w-4 h-4" />
                      <span>
                        {getOperatorWithLocation(operator, quote, aircraftDetails?.aircraft_id)}
                      </span>
                    </div> */}
                  </div>

                </div>

                {/* Price */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-accent text-primary-500 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <RiPriceTag3Line className="w-4 h-4" /> <span>{formatPrice(trunc2(aircraft.total_price || aircraft.final_price))}</span>
                  </div>
                </div>

                {/* Feature Badges */}
                <div className="flex flex-wrap gap-2">

                  {operator?.argus_rating && operator?.argus_rating!="No" &&
                  <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                    <RiTrophyLine className="h-4 w-4 text-gray-600"/> {operator?.argus_rating}
                  </span>}

                  {operator?.wyvern_rating && operator?.wyvern_rating!="No" &&
                  <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                    <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                  </span>
                  }

                  {features?.no_aog &&
                  <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
                    <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                  </span>
                  }
                </div>

                {/* Amenities */}
                <div className="mt-2">

                  <p className="text-xs text-gray-500 mb-2">Amenities</p>

                  {features?.custom_features && features?.custom_features?.length > 0 ? (
                      <div className="grid grid-cols-1 gap-1">
                        {features?.custom_features.map((feature, index) => (
                          <div key={index} className="flex border-b py-1">
                            <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                              <RiCheckLine className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">{feature}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                  ): (
                    <p className="text-xs text-gray-400">No amenities included.</p>
                  )}

                </div>
              </div>
            )}
          </div>

          {/* Finish Button - Separate Box */}
          {!isTokenBasedCheckout && (quote || invoice) &&
          <div className="mt-4">
            <Button
              variant="secondary"
              className="w-full py-3 rounded-full flex items-center justify-center gap-2 !bg-white text-gray-800 font-medium"
              onClick={handleFinish}
            >
              Finish
              <RiCheckLine className="w-5 h-5" />
            </Button>
          </div>
          }
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmed;
